// ============================================================================
// Command palette (Option D, PR-3). ⌘K / Ctrl-K — or the sidebar "Search or
// jump to…" affordance (which fires the `tcs:open-palette` window event) —
// opens a keyboard-first jump-to over the SAME role-gated nav index the
// sidebar and hubs use (nav-config.navIndex). So nav depth never blocks a
// power user: type a few letters, ↵, you're there.
//
// Results: modules first (role-gated), then facilities by name (a cheap
// one-time RLS-scoped fetch — navigates to the facility detail page). ↑/↓
// move, ↵ navigates, Esc closes. Mobile uses the More-sheet search instead.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CornerDownLeft, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from './auth';
import { supabase } from './supabase';
import { navIndex } from './nav-config';
import { useCanAccessSourcing } from './use-can-access-sourcing';
import { overlayClickClose } from './overlay-close';

interface ResultItem {
  label: string;
  path: string;
  group: string;
  icon: LucideIcon;
}

interface FacilityRow {
  facility_id: string;
  facility_name: string | null;
}

const MAX_FACILITY_RESULTS = 6;

export function CommandPalette() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const role = profile?.role;
  const { canAccess: canAccessSourcing } = useCanAccessSourcing();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [facilities, setFacilities] = useState<FacilityRow[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActive(0);
  }, []);

  // ---- Open triggers: ⌘K / Ctrl-K + the sidebar affordance event ----------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('tcs:open-palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('tcs:open-palette', onOpen);
    };
  }, []);

  // Focus the input + reset when opened.
  useEffect(() => {
    if (open) {
      setActive(0);
      // defer so the element exists
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Reload the facility list EVERY time the palette opens.
  //
  // ⛔ This used to guard on `facilities !== null`, so it fetched once per page load and
  //    then never again. The list was a snapshot taken the first time you opened the
  //    palette, and it went stale the moment anything changed: a DELETED facility kept
  //    appearing in search (that is how this was found — DaVita - Montbello was gone from
  //    Account Management and still in the palette), a renamed one kept its old name, and
  //    a newly created one was invisible until a full page refresh.
  //
  //    The previous list is deliberately NOT cleared while the refetch is in flight, so
  //    the palette still renders instantly on open and simply corrects itself. Correct and
  //    fast, rather than one or the other.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('facilities')
        .select('facility_id, facility_name')
        .order('facility_name', { ascending: true });
      if (cancelled) return;
      if (error) {
        // Graceful: the palette still navigates modules. Keep whatever we had rather
        // than blanking a working list because one refetch failed.
        setFacilities((prev) => prev ?? []);
        return;
      }
      setFacilities((data as FacilityRow[]) ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const modules = useMemo(
    () => navIndex(role, { sourcing: canAccessSourcing }),
    [role, canAccessSourcing],
  );

  const results = useMemo<ResultItem[]>(() => {
    const q = query.trim().toLowerCase();
    const moduleHits: ResultItem[] = (q
      ? modules.filter((m) => m.keywords.includes(q))
      : modules
    ).map((m) => ({ label: m.label, path: m.path, group: m.group, icon: m.icon }));

    let facilityHits: ResultItem[] = [];
    if (q.length >= 2 && facilities && facilities.length) {
      facilityHits = facilities
        .filter((f) => (f.facility_name ?? '').toLowerCase().includes(q))
        .slice(0, MAX_FACILITY_RESULTS)
        .map((f) => ({
          label: f.facility_name ?? f.facility_id,
          path: `/account-management/facilities/${f.facility_id}`,
          group: 'Facilities',
          icon: Building2,
        }));
    }
    return [...moduleHits, ...facilityHits];
  }, [modules, facilities, query]);

  // Keep active index in range as results change.
  useEffect(() => {
    setActive((a) => (results.length === 0 ? 0 : Math.min(a, results.length - 1)));
  }, [results.length]);

  const go = useCallback(
    (item: ResultItem | undefined) => {
      if (!item) return;
      close();
      navigate(item.path);
    },
    [close, navigate],
  );

  const onInputKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (results.length ? (a + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (results.length ? (a - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  if (!open) return null;

  // Group consecutive results by their group label for headings.
  let lastGroup = '';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <button type="button" aria-label="Close" className="absolute inset-0 bg-tcs-scrim/60" {...overlayClickClose(close)} />
      <div className="relative z-10 flex max-h-[70vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-tcs-border bg-tcs-bg shadow-2xl">
        <div className="flex items-center gap-2 border-b border-tcs-border px-3.5 py-3">
          <Search className="h-4 w-4 shrink-0 text-tcs-text-muted" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search modules and facilities…"
            className="w-full bg-transparent text-sm text-tcs-text placeholder:text-tcs-text-muted focus:outline-none"
          />
          <kbd className="hidden rounded border border-tcs-border bg-tcs-bg-muted px-1.5 py-0.5 font-mono text-[10px] text-tcs-text-muted sm:inline">
            esc
          </kbd>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          {results.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-tcs-text-muted">No matches.</div>
          )}
          {results.map((r, i) => {
            const showHeading = r.group !== lastGroup;
            lastGroup = r.group;
            const isActive = i === active;
            return (
              <div key={`${r.path}-${i}`}>
                {showHeading && (
                  <div className="px-3.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.08em] text-tcs-text-muted">
                    {r.group}
                  </div>
                )}
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(r)}
                  className={[
                    'flex w-full items-center gap-3 px-3.5 py-2 text-left text-sm transition-colors',
                    isActive ? 'bg-tcs-surface text-tcs-text' : 'text-tcs-text-secondary',
                  ].join(' ')}
                >
                  <r.icon className="h-4 w-4 shrink-0 text-tcs-text-muted" />
                  <span className="flex-1 truncate">{r.label}</span>
                  {isActive && <CornerDownLeft className="h-3.5 w-3.5 text-tcs-text-muted" />}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-3 border-t border-tcs-border px-3.5 py-2 text-[11px] text-tcs-text-muted">
          <span><kbd className="font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono">↵</kbd> open</span>
          <span><kbd className="font-mono">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
